/* eslint-disable  @typescript-eslint/no-explicit-any */
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import store, { snackbarActions } from '@apply/adminStore';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

export class BaseApi {
    axiosInstance: AxiosInstance;
    applicationInsights: ApplicationInsights;

    constructor(applicationInsights: ApplicationInsights) {
        if (!applicationInsights) throw new Error('API calls require a reference to Application Insights to be passed in.');

        this.applicationInsights = applicationInsights;
    }

    get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance
            .get<T>(url, config)
            .then((response) => response.data)
            .catch((error) => {
                if (error.message === "Cannot read properties of undefined (reading 'status')") {
                    return null; //Occurs when request is cancelled
                }
                error.statusCode = error.response?.status;
                error.errors = error.response?.data?.errors;
                this.handleError(error);
                return error;
            });
    }

    post<T>(url: string, postData: any = {}, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance
            .post<T>(url, postData, config)
            .then((response) => response.data)
            .catch((error) => {
                if (error.message === "Cannot read properties of undefined (reading 'status')") {
                    return null; //Occurs when request is cancelled
                }
                error.statusCode = error.response?.status;
                error.errors = error.response?.data?.errors;
                this.handleError(error);
                return error;
            });
    }

    put<T>(url: string, postData: any = {}, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance
            .put<T>(url, postData, config)
            .then((response) => response.data)
            .catch((error) => {
                if (error.message === "Cannot read properties of undefined (reading 'status')") {
                    return null; //Occurs when request is cancelled
                }
                error.statusCode = error.response?.status;
                error.errors = error.response?.data?.errors;
                this.handleError(error);
                return error;
            });
    }

    delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance
            .delete<T>(url, config)
            .then((response) => response.data)
            .catch((error) => {
                if (error.message === "Cannot read properties of undefined (reading 'status')") {
                    return null; //Occurs when request is cancelled
                }
                error.statusCode = error.response?.status;
                error.errors = error.response?.data?.errors;
                this.handleError(error);
                return error;
            });
    }

    handleError = (error) => {
        let errorMessage: string | undefined = undefined;

        // brought over from: https://axios-http.com/docs/handling_errors
        if (error.response) {
            if (error.response.status === 403) {
                errorMessage = 'Sorry, but you are not authorized to perform this action!';
            } else {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                errorMessage = error.response.data?.errors?.[0] || 'Request Failed';
            }
        } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            errorMessage = 'Failed to get a response';
        } else {
            // Something happened in setting up the request that triggered an Error
            errorMessage = error.message;
        }

        store.dispatch(
            snackbarActions.updateSnackbar({
                isOpen: true,
                message: errorMessage,
                variant: 'error',
            }) as never
        );
        this.applicationInsights.appInsights.trackException({
            error: new Error(errorMessage),
            severityLevel: SeverityLevel.Error,
        });
    };

    logError(error: string) {
        if (!error) {
            return;
        }
        this.handleError(error);
    }
}
